import { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { EffortLegend } from './EffortLegend';
import { EffortRecoveryRow } from './EffortRecoveryRow';
import { RiLegend } from './RiLegend';
import { joinEffortRecoveryRows } from './effortRecoveryJoin';
import { IEffortRow } from './effort.types';
import { useTeamRecovery } from './useTeamRecovery';
import effortStyles from './TeamEffortTab.module.scss';
import styles from './TeamEffortRecoveryView.module.scss';

interface TeamEffortRecoveryViewProps {
  teamId: string;
  /** Lunedì (YYYY-MM-DD) della settimana visualizzata — condiviso con Sforzo/Recovery. */
  weekIso: string;
  /** I 7 giorni (lun-dom) come Dayjs, per gli header allineati alle altre viste. */
  weekDays: Dayjs[];
  /** Righe Sforzo già caricate dal tab (stessa query di useTeamEffort). */
  effortRows: IEffortRow[];
  isEffortLoading: boolean;
  isEffortError: boolean;
  onSelectAthlete?: (athleteId: string, athleteName?: string) => void;
}

/**
 * Vista "Sforzo + Recovery" dentro il tab Sforzo: heatmap a cella bicolore
 * (diagonale 135°, alto-sx = sRPE, basso-dx = RI) atleti × 7 giorni, sulla
 * STESSA settimana delle altre due viste — per leggere a colpo d'occhio
 * quando sforzo e recupero divergono, senza cambiare pagina.
 */
export function TeamEffortRecoveryView({
  teamId,
  weekIso,
  weekDays,
  effortRows,
  isEffortLoading,
  isEffortError,
  onSelectAthlete,
}: TeamEffortRecoveryViewProps) {
  const {
    rows: recoveryRows,
    isLoading: isRecoveryLoading,
    isError: isRecoveryError,
    refetch,
  } = useTeamRecovery(teamId, weekIso, true);

  const rows = useMemo(
    () => joinEffortRecoveryRows(effortRows, recoveryRows, weekDays),
    [effortRows, recoveryRows, weekDays],
  );

  const isLoading = isEffortLoading || isRecoveryLoading;
  const isError = isEffortError || isRecoveryError;

  return (
    <div className={styles.root}>
      {isError ? (
        <div className={effortStyles.stateBox}>
          <p className={effortStyles.stateText}>Impossibile caricare sforzo + recovery del team.</p>
          <button className={effortStyles.retryBtn} onClick={() => refetch()}>
            Riprova
          </button>
        </div>
      ) : !isLoading && rows.length === 0 ? (
        <div className={effortStyles.stateBox}>
          <p className={effortStyles.stateText}>Nessun dato sforzo/recovery per questa settimana.</p>
        </div>
      ) : (
        <div className={effortStyles.tableWrapper}>
          <table className={effortStyles.table}>
            <thead>
              <tr>
                <th className={effortStyles.thAthlete}>Atleta</th>
                {weekDays.map((d) => (
                  <th key={d.format('YYYY-MM-DD')} className={effortStyles.thDay}>
                    <span className={effortStyles.thDayName}>{d.format('ddd D')}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className={effortStyles.stateText}>
                    Caricamento…
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <EffortRecoveryRow key={row.athlete.id} row={row} onSelect={onSelectAthlete} />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legenda combinata: bin sforzo (sRPE) + fasce RI + significato delle mezze celle, affiancate */}
      <div className={styles.combinedLegend}>
        <EffortLegend mode="srpe" />
        <RiLegend />
        <div className={effortStyles.legendItem}>
          <span className={`${effortStyles.legendSwatch} ${styles.legendSwatchNeutral}`} />
          <span className={effortStyles.legendLabel}>Metà grigia = dato mancante su un lato</span>
        </div>
        <p className={styles.combinedLegendHint}>◤ alto-sx = Sforzo · basso-dx = Recupero ◢</p>
      </div>
    </div>
  );
}

export default TeamEffortRecoveryView;
